import { z } from "zod";
import { P as PRODUCT_NAME, a as PAYMENT_FREQUENCY, b as PRODUCT_PERK, T as TripTier } from "./types-BlLW4vek.js";
import wretch from "wretch";
const featureFlagSchema = z.object({
  key: z.string(),
  state: z.enum(["on", "off"])
});
const loungeSchema = z.object({
  additional: z.string(),
  airportCode: z.string(),
  airportName: z.string(),
  city: z.string(),
  conditions: z.string(),
  country: z.string(),
  countryCode: z.string(),
  images: z.array(
    z.object({
      url: z.string().url(),
      width: z.number(),
      height: z.number(),
      altText: z.string(),
      contentType: z.string(),
      lastModified: z.string()
    })
  ),
  location: z.string(),
  loungeCode: z.string(),
  loungeName: z.string(),
  openingHours: z.string(),
  terminal: z.string(),
  terminalCode: z.string()
});
const offerSchema = z.object({
  id: z.number().nullable(),
  currency: z.string().nullable().optional(),
  // Marked native enum to document the type, it is relaxed to string because offers may change dynamically
  name: z.nativeEnum(PRODUCT_NAME).or(z.string()),
  displayName: z.string().optional(),
  paymentFrequency: z.nativeEnum(PAYMENT_FREQUENCY).nullable(),
  prices: z.record(
    z.string(),
    z.object({
      code: z.string(),
      symbol: z.string().optional(),
      label: z.string().optional(),
      price: z.any().pipe(z.coerce.number()),
      discountedPrice: z.any().pipe(z.coerce.number()).nullable().optional(),
      discountValue: z.any().pipe(z.coerce.number()).nullable().optional()
    })
  ),
  productPerks: z.array(
    z.object({
      config: z.record(z.string(), z.any()),
      credits: z.number(),
      unlimited: z.boolean(),
      // Marked native enum to document the type, it is relaxed to string because offers may change dynamically
      type: z.nativeEnum(PRODUCT_PERK).or(z.string())
    })
  ).nullable().optional().default([])
});
var BANK_INPUTS = /* @__PURE__ */ ((BANK_INPUTS2) => {
  BANK_INPUTS2["ABA"] = "aba";
  BANK_INPUTS2["ACCOUNT_NUMBER"] = "accountNumber";
  BANK_INPUTS2["CLABE"] = "clabe";
  BANK_INPUTS2["BANK_NAME"] = "bankName";
  BANK_INPUTS2["BANK_ADDRESS"] = "bankAddress";
  BANK_INPUTS2["BRANCH_NAME"] = "branchName";
  BANK_INPUTS2["BRANCH_CODE"] = "branchCode";
  BANK_INPUTS2["BANK_CODE"] = "bankCode";
  BANK_INPUTS2["BRANCH_AGENCY_CODE"] = "branchAgencyCode";
  BANK_INPUTS2["IBAN"] = "iban";
  BANK_INPUTS2["ROUTING_NUMBER"] = "routingNumber";
  BANK_INPUTS2["SWIFT"] = "swift";
  BANK_INPUTS2["TAX"] = "taxId";
  BANK_INPUTS2["BANK_CITY"] = "bankCity";
  BANK_INPUTS2["PHONE_NUMBER"] = "phoneNumber";
  BANK_INPUTS2["CHINESE_ID"] = "chineseId";
  BANK_INPUTS2["PROVINCE"] = "province";
  BANK_INPUTS2["IFSC_CODE"] = "ifscCode";
  BANK_INPUTS2["NATIONALITY"] = "nationality";
  BANK_INPUTS2["BIC_CODE"] = "bicCode";
  BANK_INPUTS2["SORT_CODE"] = "sortCode";
  BANK_INPUTS2["PAYMENT_CODE"] = "paymentCode";
  BANK_INPUTS2["NUBAN"] = "nuban";
  BANK_INPUTS2["BANK_MFO"] = "bankMfo";
  BANK_INPUTS2["BSB_CODE"] = "bsbCode";
  BANK_INPUTS2["BIN_OR_IIN"] = "binOrIin";
  BANK_INPUTS2["KNP_OR_UPDC"] = "knpOrUpdc";
  return BANK_INPUTS2;
})(BANK_INPUTS || {});
const selectInput = z.object({ label: z.string(), value: z.string() });
const bankFieldsSchema = z.object({
  aba: z.string().nullable(),
  accountNumber: z.string().nullable(),
  clabe: z.string().nullable(),
  bankName: z.string().nullable(),
  bankAddress: z.string().nullable(),
  branchName: z.string().nullable(),
  branchCode: z.string().nullable(),
  bankCode: z.string().nullable(),
  branchAgencyCode: z.string().nullable(),
  iban: z.string().nullable(),
  routingNumber: z.string().nullable(),
  swift: z.string().nullable(),
  taxId: z.string().nullable(),
  bankCity: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  chineseId: z.string().nullable(),
  province: z.string().nullable(),
  ifscCode: z.string().nullable(),
  nationality: z.string().nullable(),
  bicCode: z.string().nullable(),
  sortCode: z.string().nullable(),
  paymentCode: z.string().nullable(),
  nuban: z.string().nullable(),
  bankMfo: z.string().nullable(),
  bsbCode: z.string().nullable(),
  beneficiaryAddress: z.string().nullable(),
  beneficiaryName: z.string().nullable(),
  countryOfResidence: z.string().nullable(),
  state: z.string().nullable(),
  dateOfBirth: z.coerce.date().nullable().or(z.string().nullable()),
  streetAddress1: z.string().nullable(),
  streetAddress2: z.string().nullable(),
  city: z.string().nullable(),
  zipCode: z.string().nullable(),
  boardingPassOrBookingReference: z.string().nullable(),
  pirReference: z.string().nullable(),
  passportOrId: z.string().nullable(),
  country: selectInput.nullable(),
  residence: selectInput.nullable(),
  binOrIin: z.string().nullable(),
  knpOrUpdc: z.string().nullable()
}).partial();
const AirPayoutStatus = z.enum([
  "active",
  "ineligible",
  "eligible",
  "payout_failed",
  "processing_payment",
  "payout_successful",
  "unknown",
  "payout_returned",
  "partner_error",
  "unregistered",
  "manual_verification_pending",
  "expired"
]);
const AirPayoutDisruption = z.enum([
  "cancelation_only",
  "cancelation_delay_diversion"
]);
const AirPayoutCoverage = z.enum([
  "outbound_or_inbound",
  "outbound_only"
]);
const airPayoutJourneysSchema = z.object({
  id: z.number(),
  status: AirPayoutStatus,
  journeyId: z.number(),
  payoutAllowed: z.boolean().optional(),
  payoutUpdateAllowed: z.boolean().nullable().optional(),
  name: z.string().optional(),
  payoutRequested: z.boolean(),
  payoutAmount: z.number(),
  disruptionType: AirPayoutDisruption,
  coverageType: AirPayoutCoverage,
  ipidVersion: z.string().optional()
});
const airPayoutPaymentPaxSchema = z.object({
  id: z.number(),
  boardingPassOrBookingReference: z.string().optional(),
  boardingPassOrBookingReferenceFile: z.instanceof(File).optional(),
  passportOrId: z.string().optional(),
  passportOrIdFile: z.instanceof(File).optional()
});
const airPayoutRequestSchema = z.object({
  countryId: z.string(),
  bankFields: bankFieldsSchema,
  airPayoutJourneys: z.array(airPayoutPaymentPaxSchema)
});
const airPayoutsUpateRequestSchema = z.object({
  countryId: z.string(),
  token: z.string().optional(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema
});
const airPayoutsTriggerRequestSchema = z.object({
  countryId: z.string(),
  token: z.string().optional(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema,
  airPayoutJourneys: z.array(airPayoutPaymentPaxSchema)
});
const fellowPassengerSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string()
});
const airportSchema = z.object({
  iata: z.string(),
  city: z.string(),
  cityTranslations: z.record(z.string(), z.string()),
  timeZone: z.string(),
  name: z.string()
});
const airlineSchema = z.object({
  uniqueIdentifier: z.string().nullable(),
  code: z.string().nullable(),
  iata: z.string().nullable(),
  name: z.string(),
  displayName: z.string().nullable(),
  alternativeName: z.string().nullable()
});
const flightStatusSchema = z.object({
  departureStatus: z.string().nullable(),
  arrivalStatus: z.string().nullable(),
  gmtDepartureDateTime: z.coerce.date().nullable(),
  gmtArrivalDateTime: z.coerce.date().nullable(),
  gmtEstimatedDepartureAt: z.coerce.date().nullable(),
  gmtEstimatedArrivalAt: z.coerce.date().nullable(),
  gmtActualDepartureAt: z.coerce.date().nullable(),
  gmtActualArrivalAt: z.coerce.date().nullable()
});
const flightSchema = z.object({
  id: z.number(),
  localDepartureDate: z.coerce.date(),
  flightIdentifier: z.string(),
  flightNumber: z.string(),
  gmtDepartureDateTime: z.coerce.date().nullable(),
  gmtArrivalDateTime: z.coerce.date().nullable(),
  active: z.boolean(),
  arrivalAirport: airportSchema.nullable(),
  departureAirport: airportSchema.nullable(),
  airline: airlineSchema,
  flightStatus: flightStatusSchema.nullable()
});
const itinerarySchema = z.object({
  id: z.number(),
  remindToClaim: z.boolean(),
  claimAllowed: z.boolean(),
  active: z.boolean(),
  fellowPassengers: z.array(fellowPassengerSchema),
  flights: z.array(flightSchema)
});
const AirLuggageStatus = z.enum([
  "active",
  "eligible",
  "ineligible",
  "payout_failed",
  "processing_payment",
  "payout_successful",
  "unknown",
  "payout_returned",
  "partner_error",
  "unregistered",
  "manual_verification_pending",
  "expired"
]);
const airLuggageJourneysSchema = z.object({
  id: z.number(),
  status: AirLuggageStatus,
  journeyId: z.number(),
  availableItineraries: z.array(itinerarySchema).optional(),
  payoutAllowed: z.boolean().optional(),
  payoutUpdateAllowed: z.boolean().optional(),
  name: z.string().optional(),
  payoutRequested: z.boolean(),
  payoutAmount: z.number(),
  maxBags: z.number(),
  ipidVersion: z.string().optional(),
  transactionReference: z.string().nullable().optional()
});
const airLuggagesUpdateRequestSchema = z.object({
  countryId: z.string(),
  token: z.string().optional(),
  transactionReference: z.string().nullable().optional(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema
});
const airLuggagePaymentPaxSchema = z.object({
  id: z.number(),
  numberOfLuggages: z.number(),
  payoutEligibleItineraryId: z.number(),
  pirReference: z.string().optional(),
  pirReferenceFile: z.instanceof(File).optional(),
  boardingPassOrBookingReference: z.string().optional(),
  boardingPassOrBookingReferenceFile: z.instanceof(File).optional(),
  passportOrId: z.string().optional(),
  passportOrIdFile: z.instanceof(File).optional(),
  baggageTag: z.string().optional(),
  baggageTagFile: z.instanceof(File).optional()
});
const airLuggagesTriggerRequestSchema = z.object({
  countryId: z.string(),
  token: z.string().optional(),
  journeyId: z.number(),
  bankFields: bankFieldsSchema,
  airLuggageJourneys: z.array(airLuggagePaymentPaxSchema)
});
const tripSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  tier: TripTier.nullable(),
  startOn: z.string(),
  endOn: z.string(),
  brandName: z.string().nullable(),
  coveredPassengersQuantity: z.number(),
  upgradeable: z.boolean()
});
const AirBundleRejectionReason = z.enum(["no_credits", "rejected"]);
const airRejections = z.object({
  airPayout: AirBundleRejectionReason.optional(),
  airLuggage: AirBundleRejectionReason.optional()
});
const journeySchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  active: z.boolean(),
  loungeActive: z.boolean(),
  itineraries: z.array(itinerarySchema),
  trip: tripSchema.nullable(),
  airPayoutJourneys: z.array(airPayoutJourneysSchema),
  airLuggageJourneys: z.array(airLuggageJourneysSchema),
  airRejections: airRejections.optional(),
  displayAirBundleActionNotification: z.boolean()
});
const fetchJourneyResponseSchema = journeySchema.transform((data) => {
  var _a, _b, _c, _d;
  const hasAirPayout = data.airPayoutJourneys.length > 0;
  const hasAirLuggage = data.airLuggageJourneys.length > 0;
  const airPayoutRejected = ((_a = data.airRejections) == null ? void 0 : _a.airPayout) === AirBundleRejectionReason.enum.rejected;
  const airLuggageRejected = ((_b = data.airRejections) == null ? void 0 : _b.airLuggage) === AirBundleRejectionReason.enum.rejected;
  const airPayoutNoCredits = ((_c = data.airRejections) == null ? void 0 : _c.airPayout) === AirBundleRejectionReason.enum.no_credits;
  const airLuggageNoCredits = ((_d = data.airRejections) == null ? void 0 : _d.airLuggage) === AirBundleRejectionReason.enum.no_credits;
  const mainAirPayoutJourney = hasAirPayout ? data.airPayoutJourneys[0] : null;
  const mainAirLuggageJourney = hasAirLuggage ? data.airLuggageJourneys[0] : null;
  return {
    ...data,
    computed: {
      hasAirPayout,
      hasAirLuggage,
      airPayoutRejected,
      airLuggageRejected,
      airPayoutNoCredits,
      airLuggageNoCredits,
      mainAirPayoutJourney,
      mainAirLuggageJourney
    }
  };
});
var CheckDeletableReasons = /* @__PURE__ */ ((CheckDeletableReasons2) => {
  CheckDeletableReasons2["AIR_PAYOUT"] = "air_payout";
  CheckDeletableReasons2["AIR_LUGGAGE"] = "air_luggage";
  CheckDeletableReasons2["B2B"] = "b2b";
  CheckDeletableReasons2["LOUNGES"] = "lounges";
  return CheckDeletableReasons2;
})(CheckDeletableReasons || {});
const CheckDeletableReasonsKeys = z.enum([
  "b2b",
  "lounges",
  "air_payout",
  "air_luggage"
]);
const CheckDeletableReasonsKeysSchema = CheckDeletableReasonsKeys;
const journeyCheckDeletableSchema = z.object({
  deletable: z.boolean(),
  loungesIncluded: z.boolean(),
  blockedBy: z.array(CheckDeletableReasonsKeysSchema)
});
const journeyWithMetaSchema = z.object({
  journey: fetchJourneyResponseSchema,
  meta: z.object({
    sessionUrl: z.string(),
    airRequestId: z.number().optional()
  }).optional()
});
var InsuranceType = /* @__PURE__ */ ((InsuranceType2) => {
  InsuranceType2["AIR_PAYOUT"] = "air_payout";
  InsuranceType2["AIR_LUGGAGE"] = "air_luggage";
  return InsuranceType2;
})(InsuranceType || {});
var AirBundleDocumentFile = /* @__PURE__ */ ((AirBundleDocumentFile2) => {
  AirBundleDocumentFile2["PIR"] = "pirReference";
  AirBundleDocumentFile2["BOARDING"] = "boardingPassOrBookingReference";
  AirBundleDocumentFile2["PASSPORT"] = "passportOrId";
  AirBundleDocumentFile2["BAGGAGE_TAG"] = "baggageTag";
  return AirBundleDocumentFile2;
})(AirBundleDocumentFile || {});
var AirBundleDocumentParam = /* @__PURE__ */ ((AirBundleDocumentParam2) => {
  AirBundleDocumentParam2["PIR"] = "pir_reference";
  AirBundleDocumentParam2["BOARDING"] = "boarding_pass_or_booking_reference";
  AirBundleDocumentParam2["PASSPORT"] = "passport_or_id";
  AirBundleDocumentParam2["BAGGAGE_TAG"] = "baggage_tag";
  return AirBundleDocumentParam2;
})(AirBundleDocumentParam || {});
const airBundleDocumentUploadSchema = z.object({
  file: z.instanceof(File),
  insuranceType: z.nativeEnum(InsuranceType),
  documentType: z.nativeEnum(AirBundleDocumentParam),
  resourceId: z.number(),
  insuranceId: z.number()
});
const airBundlePresignedUrlSchema = z.object({
  fileType: z.string(),
  insuranceType: z.nativeEnum(InsuranceType),
  documentType: z.nativeEnum(AirBundleDocumentParam),
  resourceId: z.number(),
  insuranceId: z.number()
});
const airBundlePresignedUrlResponseSchema = z.object({
  fileName: z.string(),
  presignedUrl: z.string().url()
});
const verificationSchema = z.object({
  journeyId: z.number(),
  airRequestId: z.number().optional(),
  productType: z.nativeEnum(InsuranceType)
});
const verificationResponseSchema = z.object({
  airRequestId: z.number().optional(),
  sessionUrl: z.string()
});
const payment = z.object({
  type: z.string(),
  cardData: z.object({
    brand: z.string(),
    last4Digits: z.string(),
    expirationMonth: z.number(),
    expirationYear: z.number(),
    cardholderName: z.string()
  }).optional()
});
const subscription = z.object({
  nextChargeDate: z.string(),
  nextChargeAmount: z.string(),
  nextChargeCurrency: z.string(),
  nextChargeCurrencySymbol: z.string(),
  chargeFrequency: z.string()
});
const billingAddress = z.object({
  name: z.string(),
  city: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable(),
  postalCode: z.string(),
  state: z.string().nullable(),
  countryCode: z.string()
});
const fetchBillingManagementSchema = z.object({
  payment: payment.nullable(),
  subscription,
  billingAddress: billingAddress.nullable(),
  legacy: z.boolean()
});
const fetchOffersResponseSchema = z.object({
  canBuyPlus: z.boolean(),
  offers: z.array(offerSchema),
  renewOffers: z.array(offerSchema),
  upgradeOffers: z.array(offerSchema)
});
function makeFetchOffersRoute({
  baseUrl = ""
} = {}) {
  return `${baseUrl}/api/v2/offers`;
}
function fetchOffers({ client }) {
  return client.get(makeFetchOffersRoute()).valid(fetchOffersResponseSchema);
}
const fetchFeatureFlagsResponseSchema = z.object({
  features: z.array(featureFlagSchema)
}).transform((data) => {
  const transformedResponse = {};
  data.features.forEach((feature) => {
    transformedResponse[feature.key] = feature.state === "on";
  });
  return transformedResponse;
});
function fetchFeatureFlags({ client }) {
  return client.get(`/api/v2/feature_flags`).valid(fetchFeatureFlagsResponseSchema);
}
const fetchLoungesRequestSchema = z.object({
  airportCode: z.string()
});
const fetchLoungesResponseSchema = z.array(loungeSchema);
function fetchLounges({
  client,
  params
}) {
  const { airportCode } = fetchLoungesRequestSchema.parse(params);
  return client.get(`/api/v2/lounges/${airportCode}`).valid(fetchLoungesResponseSchema);
}
const fetchAirPayoutJourneysSchema = z.object({
  journeyId: z.number()
});
const fetchAirPayoutJourneysResponseSchema = z.array(airPayoutJourneysSchema).transform((data) => {
  const transformedData = data.map((item) => {
    const outboundOnly = item.coverageType === AirPayoutCoverage.enum.outbound_only;
    const cancelationOnly = item.disruptionType === AirPayoutDisruption.enum.cancelation_only;
    const showNotification = item.status === AirPayoutStatus.enum.eligible || item.status === AirPayoutStatus.enum.payout_failed || item.status === AirPayoutStatus.enum.payout_returned;
    return {
      ...item,
      computed: {
        outboundOnly,
        cancelationOnly,
        showNotification
      }
    };
  });
  return transformedData;
});
function fetchAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId } = fetchAirPayoutJourneysSchema.parse(params);
  return client.get(`/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys`).valid(fetchAirPayoutJourneysResponseSchema);
}
const fetchAirLuggageJourneysSchema = z.object({
  journeyId: z.number()
});
const fetchAirLuggageJourneysResponseSchema = z.array(airLuggageJourneysSchema).transform((data) => {
  const transformedData = data.map((item) => {
    const showNotification = item.status === AirLuggageStatus.enum.eligible || item.status === AirLuggageStatus.enum.payout_failed || item.status === AirLuggageStatus.enum.payout_returned;
    return {
      ...item,
      computed: {
        showNotification
      }
    };
  });
  return transformedData;
});
function fetchAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId } = fetchAirLuggageJourneysSchema.parse(params);
  return client.get(`/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys`).valid(fetchAirLuggageJourneysResponseSchema);
}
function triggerAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields, airPayoutJourneys } = airPayoutsTriggerRequestSchema.parse(params);
  const payload = { bankFields, countryId, airPayoutJourneys };
  return client.url(`/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys/payout`).query({ token }).snakeCaseBody(payload).post().valid(journeyWithMetaSchema);
}
function triggerAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields, airLuggageJourneys } = airLuggagesTriggerRequestSchema.parse(params);
  const payload = { bankFields, countryId, airLuggageJourneys };
  return client.url(`/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys/payout`).query({ token }).snakeCaseBody(payload).post().valid(journeyWithMetaSchema);
}
function updateAirPayoutJourneys({
  client,
  params
}) {
  const { journeyId, token, countryId, bankFields } = airPayoutsUpateRequestSchema.parse(params);
  const payload = { bankFields, countryId };
  return client.url(
    `/api/v2/air_bundle/journeys/${journeyId}/air_payout_journeys/payout_update`
  ).snakeCaseBody(payload).query({ token }).patch().valid(fetchJourneyResponseSchema);
}
function updateAirLuggageJourneys({
  client,
  params
}) {
  const { journeyId, token, transactionReference, countryId, bankFields } = airLuggagesUpdateRequestSchema.parse(params);
  const payload = { bankFields, countryId, transactionReference };
  return client.url(
    `/api/v2/air_bundle/journeys/${journeyId}/air_luggage_journeys/payout_update`
  ).query({ token }).snakeCaseBody(payload).patch().valid(fetchJourneyResponseSchema);
}
function getPresignedUrl({
  client,
  params
}) {
  return client.url(`/api/v2/s3/presigned_url`).snakeCaseBody(airBundlePresignedUrlSchema.parse(params)).post().valid(airBundlePresignedUrlResponseSchema);
}
async function uploadAirBundleDocument({
  client,
  params
}) {
  const { file, insuranceType, documentType, resourceId, insuranceId } = airBundleDocumentUploadSchema.parse(params);
  const apiParams = {
    fileType: file.type,
    insuranceType,
    documentType,
    resourceId,
    insuranceId
  };
  return await getPresignedUrl({ client, params: apiParams }).then(
    async (apiResponse) => {
      const { presignedUrl, fileName } = airBundlePresignedUrlResponseSchema.parse(apiResponse);
      return await wretch(presignedUrl).body(file).put().res((s3response) => {
        if (s3response.status === 200) {
          return fileName;
        }
        throw new Error("Failed to upload document");
      });
    }
  );
}
function createVerificationSession({
  client,
  params
}) {
  return client.url(`/api/v2/air_bundle/identity_verifications}`).snakeCaseBody(verificationSchema.parse(params)).post().valid(verificationResponseSchema);
}
const postPromoCodeSchema = z.object({
  productName: z.string(),
  promoCode: z.string(),
  paymentIntentId: z.string(),
  currency: z.string()
});
const postPromoCodeResponseSchema = z.object({
  currency: z.string(),
  discountValue: z.string(),
  discountedPrice: z.string(),
  productPrice: z.string()
});
function postPromoCode({
  client,
  params
}) {
  return client.url(`/api/v2/promo_campaigns/apply_code`).snakeCaseBody(postPromoCodeSchema.parse(params)).post().valid(postPromoCodeResponseSchema);
}
const fetchJourneySchema = z.object({
  id: z.number()
});
function fetchJourney({
  client,
  params
}) {
  const { id } = fetchJourneySchema.parse(params);
  return client.get(`/api/v2/journeys/${id}`).valid(fetchJourneyResponseSchema);
}
const fetchJourneysResponseSchema = z.array(fetchJourneyResponseSchema);
function fetchJourneys({ client }) {
  return client.get(`/api/v2/journeys`).valid(fetchJourneysResponseSchema);
}
const fetchCheckDeletableJourneySchema = z.object({
  id: z.number()
});
const fetchCheckDeletableJourneyResponseSchema = journeyCheckDeletableSchema.transform((data) => {
  const blockedByB2b = data.blockedBy.includes(CheckDeletableReasons.B2B);
  const blockedByLounges = data.blockedBy.includes(
    CheckDeletableReasons.LOUNGES
  );
  const blockedByAirPayout = data.blockedBy.includes(
    CheckDeletableReasons.AIR_PAYOUT
  );
  const blockedByAirLuggage = data.blockedBy.includes(
    CheckDeletableReasons.AIR_LUGGAGE
  );
  return {
    ...data,
    computed: {
      blockedByB2b,
      blockedByLounges,
      blockedByAirPayout,
      blockedByAirLuggage
    }
  };
});
function fetchCheckDeletableJourney({
  client,
  params
}) {
  const { id } = fetchCheckDeletableJourneySchema.parse(params);
  return client.get(`/api/v2/journeys/${id}/check_deletable`).valid(fetchCheckDeletableJourneyResponseSchema);
}
const deleteJourneySchema = z.object({
  id: z.number()
});
function deleteJourney({
  client,
  params
}) {
  const { id } = deleteJourneySchema.parse(params);
  return client.url(`/api/v2/journeys/${id}`).delete().text();
}
const updateJourneySchema = z.object({
  name: z.string()
});
const updateJourneyRequestSchema = z.object({
  journey: fetchJourneyResponseSchema,
  payload: updateJourneySchema
});
function updateJourney({
  client,
  params
}) {
  const { journey, payload } = updateJourneyRequestSchema.parse(params);
  const id = journey.id;
  return client.url(`/api/v2/journeys/${id}`).patch(payload).valid(fetchJourneyResponseSchema);
}
const minDateSchema = z.union([z.date(), z.string(), z.null()]);
const maxDateSchema = z.union([z.date(), z.string(), z.null()]);
const editableFlightSchema = z.object({
  id: z.number(),
  departureAirport: airportSchema.nullable(),
  departureAirportCode: z.string().nullable(),
  arrivalAirport: airportSchema.nullable(),
  arrivalAirportCode: z.string().nullable(),
  airline: airlineSchema.nullable(),
  airlineIdentifier: z.string().nullable(),
  flightNumber: z.string().nullable(),
  localDepartureDate: z.coerce.date().nullable(),
  metadata: z.object({
    minDate: minDateSchema,
    maxDate: maxDateSchema
  })
});
const editableFellowPassengerSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string()
});
const editableItinerarySchema = z.object({
  id: z.number(),
  fellowPassengers: z.array(editableFellowPassengerSchema),
  flights: z.array(editableFlightSchema)
});
const editableJourneySchema = z.object({
  name: z.string().nullable().optional(),
  itineraries: z.array(editableItinerarySchema),
  applyAirLuggage: z.boolean()
});
const editableJourneyRequestSchema = z.object({
  token: z.string().optional(),
  payload: editableJourneySchema
});
const addJourneyResponseSchema = journeySchema;
function addJourney({
  client,
  params
}) {
  const { token = "", payload } = editableJourneyRequestSchema.parse(params);
  return client.url(`/api/v2/journeys`).query({ token }).snakeCaseBody(payload).post().valid(addJourneyResponseSchema);
}
const cancelMembershipSchema = z.object({});
const cancelMembershipResponseSchema = z.object({});
function cancelMembership({
  client,
  params
}) {
  return client.url(`/api/v2/membership/cancel`).patch(cancelMembershipSchema.parse(params)).valid(cancelMembershipResponseSchema);
}
const tripResponseSchema = z.object({
  trip: z.object({
    id: z.number(),
    uuid: z.string(),
    tier: TripTier,
    startOn: z.string(),
    endOn: z.string(),
    brandName: z.string().nullable(),
    coveredPassengersQuantity: z.number(),
    upgradeable: z.boolean()
  })
});
const fetchTripRequestSchema = z.object({
  uuid: z.string()
});
function fetchTrip({
  client,
  params
}) {
  const { uuid } = fetchTripRequestSchema.parse(params);
  return client.get(`/api/v2/trips/${uuid}`).valid(tripResponseSchema);
}
function fetchBillingManagement({ client }) {
  return client.get("/api/v2/billing_management").valid(fetchBillingManagementSchema);
}
const updateBillingAddressResponseSchema = z.object({});
const updateBillingAddressSchema = z.object({
  name: z.string(),
  address: z.object({
    line1: z.string(),
    line2: z.string().nullable(),
    city: z.string(),
    country: z.string(),
    postal_code: z.string(),
    state: z.string().nullable()
  })
});
const updateBillingAddressRequestSchema = z.object({
  payload: updateBillingAddressSchema
});
function updateBillingAddress({
  client,
  params
}) {
  const { payload } = updateBillingAddressRequestSchema.parse(params);
  return client.url("/api/v2/billing_management/update_address").patch(payload).valid(updateBillingAddressResponseSchema);
}
const updateCreditCardResponseSchema = z.object({
  clientSecret: z.string(),
  setupIntentId: z.string()
});
function updateCreditCard({ client }) {
  return client.url("/api/v2/billing_management").post().valid(updateCreditCardResponseSchema);
}
export {
  airlineSchema as $,
  tripSchema as A,
  AirBundleRejectionReason as B,
  airRejections as C,
  journeySchema as D,
  fetchJourneyResponseSchema as E,
  CheckDeletableReasons as F,
  journeyCheckDeletableSchema as G,
  journeyWithMetaSchema as H,
  AirPayoutStatus as I,
  AirPayoutDisruption as J,
  AirPayoutCoverage as K,
  airPayoutJourneysSchema as L,
  airPayoutRequestSchema as M,
  airPayoutsUpateRequestSchema as N,
  airPayoutsTriggerRequestSchema as O,
  AirLuggageStatus as P,
  airLuggageJourneysSchema as Q,
  airLuggagesUpdateRequestSchema as R,
  airLuggagesTriggerRequestSchema as S,
  InsuranceType as T,
  AirBundleDocumentFile as U,
  AirBundleDocumentParam as V,
  airBundleDocumentUploadSchema as W,
  airBundlePresignedUrlSchema as X,
  airBundlePresignedUrlResponseSchema as Y,
  fellowPassengerSchema as Z,
  airportSchema as _,
  fetchOffers as a,
  flightStatusSchema as a0,
  flightSchema as a1,
  itinerarySchema as a2,
  BANK_INPUTS as a3,
  bankFieldsSchema as a4,
  verificationSchema as a5,
  verificationResponseSchema as a6,
  payment as a7,
  subscription as a8,
  billingAddress as a9,
  fetchBillingManagement as aA,
  updateBillingAddressResponseSchema as aB,
  updateBillingAddressSchema as aC,
  updateBillingAddressRequestSchema as aD,
  updateBillingAddress as aE,
  updateCreditCardResponseSchema as aF,
  updateCreditCard as aG,
  fetchBillingManagementSchema as aa,
  fetchJourneySchema as ab,
  fetchJourney as ac,
  fetchJourneysResponseSchema as ad,
  fetchJourneys as ae,
  fetchCheckDeletableJourneySchema as af,
  fetchCheckDeletableJourneyResponseSchema as ag,
  fetchCheckDeletableJourney as ah,
  deleteJourneySchema as ai,
  deleteJourney as aj,
  updateJourneySchema as ak,
  updateJourneyRequestSchema as al,
  updateJourney as am,
  editableFlightSchema as an,
  editableFellowPassengerSchema as ao,
  editableItinerarySchema as ap,
  editableJourneySchema as aq,
  editableJourneyRequestSchema as ar,
  addJourneyResponseSchema as as,
  addJourney as at,
  cancelMembershipSchema as au,
  cancelMembershipResponseSchema as av,
  cancelMembership as aw,
  tripResponseSchema as ax,
  fetchTripRequestSchema as ay,
  fetchTrip as az,
  fetchFeatureFlagsResponseSchema as b,
  fetchFeatureFlags as c,
  fetchLoungesRequestSchema as d,
  fetchLoungesResponseSchema as e,
  fetchOffersResponseSchema as f,
  fetchLounges as g,
  fetchAirPayoutJourneysSchema as h,
  fetchAirPayoutJourneysResponseSchema as i,
  fetchAirPayoutJourneys as j,
  fetchAirLuggageJourneysSchema as k,
  fetchAirLuggageJourneysResponseSchema as l,
  makeFetchOffersRoute as m,
  fetchAirLuggageJourneys as n,
  triggerAirLuggageJourneys as o,
  updateAirLuggageJourneys as p,
  uploadAirBundleDocument as q,
  createVerificationSession as r,
  postPromoCodeSchema as s,
  triggerAirPayoutJourneys as t,
  updateAirPayoutJourneys as u,
  postPromoCodeResponseSchema as v,
  postPromoCode as w,
  featureFlagSchema as x,
  loungeSchema as y,
  offerSchema as z
};
//# sourceMappingURL=update-credit-card-BEyMBumU.js.map
